import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Primitive } from '@radix-ui/react-primitive';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import usePopperPresence from '../../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../../hooks/usePopperZIndexStyle.mjs';
import { overlayStyle } from '../../../../utils/overlay.vanilla.mjs';
import InjectAside from '../../../utility/InjectAside.mjs';
import { useModalContext } from '../../context.mjs';
import { contentPositioner } from '../../styles.vanilla.mjs';
import { styledContentLegacy, contentRootLegacy } from './contentLegacy.vanilla.mjs';
import { StyledContent_Legacy } from './styled.mjs';

const ModalContent = forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      leftAside,
      rightAside,
      overlayTheme = "light",
      className: classNameFromProps,
      portalContainer: portalContainerFromProps,
      zIndex,
      ...restProps
    } = props;
    const {
      modal,
      open,
      onClose,
      hasLeftAsideChildren,
      hasRightAsideChildren,
      onChangeModalContentElement,
      portalContainerElement: portalContainerElementFromContext
    } = useModalContext("Modal.Content_Legacy");
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (isPresent) => !isPresent && onClose?.()
    });
    const composedRef = useComposedRefs(
      forwardedRef,
      presenceRef,
      onChangeModalContentElement
    );
    const portalContainer = portalContainerFromProps ?? portalContainerElementFromContext;
    const positionStyle = useMemo(() => {
      if (isNil(portalContainer)) {
        return;
      }
      return { position: "absolute" };
    }, [portalContainer]);
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    const positionerStyle = Object.assign({}, zIndexStyle, positionStyle);
    const className = classNames(styledContentLegacy, classNameFromProps);
    return /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { container: portalContainer, children: [
      /* @__PURE__ */ jsx(
        DialogPrimitive.Overlay,
        {
          className: overlayStyle({ overlayTheme }),
          style: positionerStyle
        }
      ),
      /* @__PURE__ */ jsx(
        Primitive.div,
        {
          className: contentPositioner({ placement: "center", modal }),
          style: positionerStyle,
          children: /* @__PURE__ */ jsxs(
            StyledContent_Legacy,
            {
              ref: composedRef,
              className,
              ...restProps,
              children: [
                leftAside ? /* @__PURE__ */ jsx(InjectAside, { side: "left", children: leftAside }) : null,
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: contentRootLegacy({
                      layout: hasLeftAsideChildren || hasRightAsideChildren ? "column" : void 0
                    }),
                    children
                  }
                ),
                rightAside ? /* @__PURE__ */ jsx(InjectAside, { side: "right", children: rightAside }) : null
              ]
            }
          )
        }
      )
    ] });
  }
);

export { ModalContent as ModalContent_Legacy };
//# sourceMappingURL=Content_Legacy.mjs.map
